import { ConfigProvider } from "antd"
import React, { type HTMLAttributes, useMemo } from "react"

import { defaultTheme } from "../../../theme.ts"
import { cn } from "../../../utils/cn.ts"
import "./automation-surface.css"

const BRAND_TEAL = defaultTheme.token?.colorPrimary ?? "#2A7D8C"

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * The automation pages' own surface: the off-white page the white sections sit
 * on, and scoped tokens that lift AntD's pale helper (3.4:1), placeholder
 * (2.7:1) and blue link text to AA on white, keep links in the brand teal, and
 * drop AntD's motion when the user asks for less. Nested, so it merges onto
 * the app theme and touches nothing outside these pages. Both automation
 * pages fill the Settings content edge to edge, with no outer width cap.
 */
export function AutomationSurface({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  const reducedMotion = useMemo(prefersReducedMotion, [])
  return (
    <ConfigProvider
      theme={{
        token: {
          colorTextDescription: "#4B5563",
          colorTextPlaceholder: "#6B7280",
          colorLink: BRAND_TEAL,
          colorLinkHover: "#1F6470",
          colorLinkActive: "#1F6470",
          controlHeight: 40,
          motion: !reducedMotion
        }
      }}
    >
      <div
        {...rest}
        className={cn("automation-scope w-full min-w-0 rounded-xl bg-background p-3 sm:p-4", className)}
      >
        {children}
      </div>
    </ConfigProvider>
  )
}
